import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Checkbox, Switch } from '@progress/kendo-react-inputs';
import React, { useEffect, useState } from 'react';
import { AddOutline, DragOutline, EditOutline, TrashOutline } from '../../../../../icons/icons';
import { UDViewPlaneFilterSettingModel } from '../../../../../udView/udview';
import { useEvent } from '../../../../contexts/event-bus';
import { useAppDispatch } from '../../../../hooks/store-hooks';
import { patchFocusedWorkspaceView } from '../../../../state/store';
import { useWorkspaceView } from '../../hooks/use-view';
import { FlyoutActions, useFlyoutHeader } from '../Flyout';
import { FilterFlyoutEdit } from './FilterFlyoutEdit';

export default function FiltersFlyout() {
    const [isEdit, setIsEdit] = React.useState(false);
    const [editObject, setEditObject] = React.useState<{
        filter: Partial<UDViewPlaneFilterSettingModel>;
        index: number;
    } | null>(null);
    const [template, setTemplate] = React.useState<Partial<UDViewPlaneFilterSettingModel> | null>(null);
    const context = useFlyoutHeader();
    const handleAdd = React.useCallback(() => {
        setIsEdit(true);
        setEditObject(null);
        context.setSubMenu({
            title: 'Create a new filter',
            onBack: () => {
                setIsEdit(false);
            }
        });
    }, []);

    const handleAddFromTemplate = React.useCallback((template?: Partial<UDViewPlaneFilterSettingModel>) => {
        setTemplate(template || null);
        handleAdd();
    }, []);

    useEvent('add-new-filter', ({ template }) => handleAddFromTemplate(template));

    return (
        <div id="filters-flyout" className='h-100 min-h-0 d-flex flex-column'>
            <FlyoutActions>
                {!isEdit && (
                    <Button
                        id="filter-add-btn"
                        title="New a new filter"
                        svgIcon={AddOutline}
                        className="button-small"
                        fillMode="flat"
                        onClick={() => handleAdd()}
                    />
                )}
            </FlyoutActions>

            {!isEdit && (
                <FiltersFlyoutList
                    onEdit={(filter) => {
                        setEditObject(filter);
                        setIsEdit(true);
                    }}
                />
            )}
            {isEdit && (
                <FilterFlyoutEdit
                    edit={editObject}
                    template={template}
                    close={() => {
                        setIsEdit(false);
                        context.setSubMenu(null);
                    }}
                />
            )}
        </div>
    );
}

function FiltersFlyoutList(props: { onEdit?: (filter: { filter: UDViewPlaneFilterSettingModel; index: number }) => void }) {
    const filter = useWorkspaceView((x) => x.plane.filter);
    const dispatch = useAppDispatch();
    const [filterIndexes, setFilterIndexes] = useState(filter.planeFilterSettings.map((_, i) => i));
    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        setFilterIndexes(filter.planeFilterSettings.map((_, i) => i));
    }, [filter]);

    const handleDragEnd = React.useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;

            if (over && active.id !== over.id) {
                setFilterIndexes((items) => {
                    const oldIndex = items.indexOf(active.id as number);
                    const newIndex = items.indexOf(over.id as number);
                    return arrayMove(items, oldIndex, newIndex);
                });
                dispatch(
                    patchFocusedWorkspaceView((view) => {
                        view.plane.filter.planeFilterSettings = arrayMove(
                            view.plane.filter.planeFilterSettings,
                            active.id as number,
                            over.id as number
                        );
                    })
                );
            }
        },
        [dispatch]
    );

    return (
        <div className="p-2 px-3 overflow-auto" style={{ minWidth: 300 }}>
            <div className="d-flex justify-content-between pb-2 align-items-center border-bottom" id="filters-apply-filters">
                <div>Apply filters</div>
                <Switch
                    checked={filter.useFilters}
                    onChange={(e) =>
                        dispatch(
                            patchFocusedWorkspaceView((v) => {
                                v.plane.filter.useFilters = e.value;
                            })
                        )
                    }
                />
            </div>
            <div id="filter-list">
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    modifiers={[restrictToParentElement]}>
                    <SortableContext items={filterIndexes} strategy={verticalListSortingStrategy}>
                        {filter.planeFilterSettings.map((f) => (
                            <Filter id={filter.planeFilterSettings.indexOf(f)} filter={f} onEdit={props.onEdit} />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>

            {filter.planeFilterSettings.length === 0 && <div className="pt-2">No filters available</div>}
        </div>
    );
}

function Filter(props: {
    id: number;
    filter: UDViewPlaneFilterSettingModel;
    onEdit?: (filter: { filter: UDViewPlaneFilterSettingModel; index: number }) => void;
}) {
    const dispatch = useAppDispatch();
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: props.id });
    const filter = props.filter;

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };

    const handleDelete = React.useCallback((index: number) => {
        dispatch(
            patchFocusedWorkspaceView((view) => {
                view.plane.filter.planeFilterSettings.splice(index, 1);
                if (view.plane.filter.planeFilterSettings.length === 0) {
                    view.plane.filter.useFilters = false;
                }
            })
        );
    }, []);

    return (
        <div ref={setNodeRef} style={style}>
            <div key={props.id} className="d-flex pt-1 align-items-center">
                <Checkbox
                    value={filter.isActive}
                    onChange={(e) =>
                        dispatch(
                            patchFocusedWorkspaceView((v) => {
                                v.plane.filter.planeFilterSettings[props.id].isActive = e.value;
                            })
                        )
                    }
                />
                <div className="ms-1 border" style={{ height: 18, width: 18, backgroundColor: filter.color }}></div>
                <div className="px-1 flex-grow-1 align-items-center">{filter.name}</div>
                <Button
                    className="button-small filter-edit-btn"
                    fillMode="flat"
                    svgIcon={EditOutline}
                    onClick={() => props.onEdit?.({ filter, index: props.id })}
                />
                <Button
                    className="button-small filter-delete-btn"
                    fillMode="flat"
                    svgIcon={TrashOutline}
                    onClick={() => handleDelete(props.id)}
                />
                <SvgIcon icon={DragOutline} {...attributes} {...listeners} svgStyle={{ outline: 'none' }} className="filter-reorder-btn" />
            </div>
        </div>
    );
}
